import React, { useMemo } from "react";
import { Button, ConfigProvider, Modal, Table, Tag, message, Spin } from "antd";
import style from "css/admin/component.module.css";
import axios from "axios";

function ApproveModal({ open, onCancel, rows, applicationIds, onSuccess, taskType = "approve", notice = "" }) {
  const [loading, setLoading] = React.useState(false);
  const isReRequest = taskType === "rerequest";
  const modalTitle = isReRequest ? "재요청" : "승인";
  const confirmText = isReRequest
    ? "아래 선택된 신청 건들을 재요청하시겠습니까?"
    : "아래 선택된 신청 건들을 승인하시겠습니까?";
  const submitText = isReRequest ? "재요청" : "승인";
  const failText = isReRequest ? "재요청에 실패했습니다." : "승인 요청에 실패했습니다.";
  const errorPrefix = isReRequest ? "재요청 오류" : "승인 오류";

  const dataSource = useMemo(() => {
    return (rows || []).map((r) => ({
      key: r?.APPLICATION_ID ?? `${r?.MEMBER_ID || ""}_${r?.PRODUCT_CD || ""}`,
      APPLICATION_ID: r?.APPLICATION_ID,
      MEMBER_ID: r?.MEMBER_ID,
      MEMBER_NM: r?.MEMBER_NM,
      DEPARTMENT_NM: r?.DEPARTMENT_NM,
      LICENSE_NM: [r?.MANUFACTURER_NM, r?.PRODUCT_NM].filter(Boolean).join(" "),
      STATUS: r?.STATUS,
      STATUS_LABEL: r?.STATUS_LABEL,
    }));
  }, [rows]);

  const renderStatusTag = (statusCode, label) => {
    const code = statusCode == null ? "" : String(statusCode).trim();
    const text = label ? String(label) : (code || "-");
    if (code === "CD0205") {
      return (
        <Tag color="#fff" className={style.status_tag} style={{ backgroundColor: "#444" }}>
          {text}
        </Tag>
      );
    }
    if (code === "CD0204") {
      return (
        <Tag color="#595959" className={style.status_tag} style={{ color: "#000" }}>
          {text}
        </Tag>
      );
    }
    if (code === "CD0201") return <Tag color="processing" className={style.status_tag}>{text}</Tag>;
    if (code === "CD0202") return <Tag color="warning" className={style.status_tag}>{text}</Tag>;
    if (code === "CD0203") return <Tag color="error" className={style.status_tag}>{text}</Tag>;
    return <Tag color="default" className={style.status_tag}>{text}</Tag>;
  };

  const columns = useMemo(
    () => [
      { title: "학번", dataIndex: "MEMBER_ID", key: "MEMBER_ID", width: 110 },
      { title: "이름", dataIndex: "MEMBER_NM", key: "MEMBER_NM", width: 120 },
      { title: "학과", dataIndex: "DEPARTMENT_NM", key: "DEPARTMENT_NM", width: 160 },
      { title: "라이선스", dataIndex: "LICENSE_NM", key: "LICENSE_NM" },
      {
        title: "상태",
        dataIndex: "STATUS",
        key: "STATUS",
        width: 90,
        align: "center",
        render: (code, record) => renderStatusTag(code, record?.STATUS_LABEL),
      },
    ],
    []
  );

  const approve = async () => {
    if (!Array.isArray(applicationIds) || applicationIds.length === 0) {
      message.warning("선택된 신청건이 없습니다.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post("/application/approve", {
        applicationIds,
        testMode: false,
      });

      if (res.status === 200) {
        if (typeof onSuccess === "function") onSuccess(res.data);
      } else {
        message.error(failText);
      }
    } catch (err) {
      const serverMsg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.response?.data ||
        err?.message;
      message.error(serverMsg ? `${errorPrefix}: ${serverMsg}` : `${modalTitle} 중 오류가 발생했습니다.`);
      // eslint-disable-next-line no-console
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<span className={style.searchItem_label} style={{ fontSize: 18 }}>{modalTitle}</span>}
      open={open}
      onCancel={onCancel}
      closable={false}
      maskClosable={!loading}
      footer={
        <ConfigProvider
          theme={{
            token: { colorPrimary: "#527ff2" },
            components: {
              Button: {
                colorPrimaryHover: "#4a72de",
                colorPrimaryActive: "#3f66c8",
              },
            },
          }}
        >
          <div style={{ display: "flex", gap: 8, width: "100%" }}>
            <Button
              key="cancel"
              onClick={onCancel}
              disabled={loading}
              style={{ flex: 1, height: 40, borderRadius: 5, fontSize: 18 }}
            >
              취소
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={approve}
              loading={loading}
              style={{ flex: 1, height: 40, borderRadius: 5, fontSize: 18 }}
            >
              {submitText}
            </Button>
          </div>
        </ConfigProvider>
      }
      styles={{ content: { borderRadius: 12 }, mask: { backdropFilter: "none" } }}
      width={760}
      centered
    >
      <div style={{ display: 'flex', gap: 5, marginBottom: 12, alignItems: 'center' }}>
        <div style={{ fontSize: 14, color: "#33353b" }}>
          {confirmText}
        </div>
        {!isReRequest && notice ? (
          <div style={{ fontSize: 12, color: "#f65c22" }}>
            ({notice})
          </div>
        ) : null}
      </div>
      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={dataSource}
          size="small"
          pagination={false}
          scroll={{ y: 320 }}
        />
      </Spin>
    </Modal >
  );
}

export default ApproveModal;


